import {
  createClient,
  LoginRequiredError,
  NoSessionError,
  RefreshError,
  RefreshTimeoutError,
} from "@workos-inc/authkit-js";
import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import { computed, ref, shallowRef } from "vue";

type SDK = Awaited<ReturnType<typeof createClient>>;
type SDKUser = NonNullable<ReturnType<SDK["getUser"]>>;
type Session = {
  sub: string;
  org_id?: string;
  role?: string;
  analytics_id: string;
  generation: number;
};
type Status =
  | "initializing"
  | "signedOut"
  | "completingLogin"
  | "ready"
  | "noOrganization"
  | "retryableFailure"
  | "signInFailed";
type Options = {
  clientId: string;
  redirectUri: string;
  logoutReturnUri?: string;
  apiHostname: string;
  devMode: boolean;
  api: AxiosInstance;
  resetTenantState: () => void;
  resetAnalytics: () => void;
  identifyAnalytics: (id: string) => void;
  navigate: (target: string) => Promise<unknown>;
  reevaluateRoutes: () => Promise<unknown>;
};
type RequestConfig = InternalAxiosRequestConfig & {
  authGeneration?: number;
  authRetry?: boolean;
  authCompletion?: boolean;
  authController?: AbortController;
};

export const normalizeRedirectTarget = (
  target: unknown,
): string | undefined => {
  if (typeof target !== "string") return undefined;
  let decoded = target;
  for (let index = 0; index <= target.length; index += 1) {
    if (
      !decoded.startsWith("/") ||
      decoded.startsWith("//") ||
      /[\\\u0000-\u001f\u007f]/.test(decoded)
    )
      return undefined;
    try {
      const next = decodeURIComponent(decoded);
      if (next === decoded) return target;
      decoded = next;
    } catch {
      return undefined;
    }
  }
  return undefined;
};

export const createWorkOSAuth = (options: Options) => {
  const status = ref<Status>("initializing");
  const message = ref("");
  const user = shallowRef<SDKUser | null>(null);
  const session = shallowRef<Session | null>(null);
  let generation = 0;
  let sdkPromise: Promise<SDK> | undefined;
  let initialization: Promise<void> | undefined;
  let switching: { organizationId: string; promise: Promise<void> } | undefined;
  let completing: { generation: number; promise: Promise<void> } | undefined;
  let refreshing: Promise<string> | undefined;
  let logout: Promise<void> | undefined;
  let signingIn: Promise<void> | undefined;
  let reevaluation: { generation: number; promise: Promise<void> } | undefined;
  let callbackTarget: string | undefined;
  let roleRefreshPending = false;
  let tokenRefreshPending = false;
  let completedGeneration = -1;
  const controllers = new Set<AbortController>();
  const callbackPresent =
    new URLSearchParams(window.location.search).has("code") ||
    new URLSearchParams(window.location.search).has("error");
  const callbackError = new URLSearchParams(window.location.search).has(
    "error",
  );
  const cleanCallback = () => {
    if (!callbackPresent) return;
    const url = new URL(window.location.href);
    for (const key of [
      "code",
      "state",
      "error",
      "error_description",
      "error_uri",
    ])
      url.searchParams.delete(key);
    window.history.replaceState(window.history.state, "", url);
  };

  const invalidate = () => {
    generation += 1;
    for (const controller of controllers) controller.abort();
    controllers.clear();
    session.value = null;
    user.value = null;
    roleRefreshPending = false;
    tokenRefreshPending = false;
    options.resetTenantState();
    options.resetAnalytics();
  };
  const assertCurrent = (expected: number) => {
    if (generation !== expected)
      throw new axios.CanceledError("Session changed");
  };
  const sdk = () => {
    sdkPromise ??= createClient(options.clientId, {
      redirectUri: options.redirectUri,
      apiHostname: options.apiHostname,
      devMode: options.devMode,
      onRedirectCallback: (result) => {
        callbackTarget = normalizeRedirectTarget(
          (result.state as { returnTo?: unknown } | undefined)?.returnTo,
        );
      },
      onRefresh: (result) => {
        if (logout || status.value === "signedOut") return;
        const previous = session.value;
        publish(result.accessToken, result.user);
        if (previous && previous.generation !== generation) {
          const expected = generation;
          void complete().catch(() => {
            if (generation === expected) {
              status.value = "retryableFailure";
              message.value = "Please try again.";
            }
          });
        }
        if (
          previous &&
          previous.generation === generation &&
          previous.role !== session.value?.role &&
          completedGeneration === generation &&
          !roleRefreshPending &&
          !switching
        ) {
          const expected = generation;
          void reevaluateRole().catch(() => {
            if (generation === expected) {
              status.value = "retryableFailure";
              message.value = "Please try again.";
            }
          });
        }
      },
      onRefreshFailure: () => {
        if (status.value === "signedOut") return;
        invalidate();
        status.value = "signedOut";
        message.value = "";
      },
    });
    return sdkPromise;
  };
  const publish = (accessToken: string, profile: SDKUser | null) => {
    const segment = accessToken.split(".")[1];
    if (!segment || !profile) throw new LoginRequiredError();
    const claims = JSON.parse(
      atob(segment.replace(/-/g, "+").replace(/_/g, "/")),
    ) as Record<string, unknown>;
    if (
      typeof claims.sub !== "string" ||
      claims.sub !== profile.id ||
      typeof claims.analytics_id !== "string" ||
      !claims.analytics_id
    )
      throw new Error("Invalid session identity");
    if (
      claims.org_id !== undefined &&
      (typeof claims.org_id !== "string" ||
        !claims.org_id ||
        !["admin", "member"].includes(String(claims.role)))
    )
      throw new Error("Invalid organization session");
    const previous = session.value;
    if (
      previous &&
      (previous.sub !== claims.sub || previous.org_id !== claims.org_id)
    ) {
      invalidate();
      status.value = "initializing";
    }
    user.value = profile;
    session.value = {
      sub: claims.sub,
      org_id: claims.org_id as string | undefined,
      role: claims.role as string | undefined,
      analytics_id: claims.analytics_id,
      generation,
    };
  };
  const signOut = async (
    request: { navigate?: boolean } = {},
  ): Promise<void> => {
    invalidate();
    status.value = "signedOut";
    message.value = "";
    if (logout) return logout;
    logout = (async () => {
      try {
        const client = await sdk();
        if (request.navigate === false)
          await client.signOut({ navigate: false });
        else
          await client.signOut({
            returnTo: options.logoutReturnUri ?? options.redirectUri,
          });
      } catch (error) {
        if (!(error instanceof NoSessionError)) throw error;
      }
    })().finally(() => {
      logout = undefined;
    });
    return logout;
  };
  const handleRefreshError = async (error: unknown, expected: number) => {
    assertCurrent(expected);
    if (
      error instanceof LoginRequiredError ||
      (error instanceof RefreshError && !error.isTransient)
    )
      await signOut();
    else if (
      error instanceof RefreshTimeoutError ||
      error instanceof RefreshError ||
      error instanceof TypeError
    ) {
      tokenRefreshPending = true;
      status.value = "retryableFailure";
      message.value = "Please try again.";
    }
    throw error;
  };
  const getAccessToken = async (request?: {
    forceRefresh?: boolean;
  }): Promise<string> => {
    if (request?.forceRefresh && refreshing) return refreshing;
    const expected = generation;
    const operation = (async () => {
      try {
        const client = await sdk();
        const accessToken = await client.getAccessToken(request);
        assertCurrent(expected);
        publish(accessToken, client.getUser() ?? null);
        if (request?.forceRefresh) tokenRefreshPending = false;
        return accessToken;
      } catch (error) {
        return handleRefreshError(error, expected);
      }
    })();
    if (!request?.forceRefresh) return operation;
    refreshing = operation.finally(() => {
      refreshing = undefined;
    });
    return refreshing;
  };
  const complete = (): Promise<void> => {
    const expected = generation;
    if (completing?.generation === expected) return completing.promise;
    const promise = (async () => {
      if (!session.value?.org_id) {
        status.value = "noOrganization";
        message.value = "No organization assigned.";
        return;
      }
      if (completedGeneration === expected) {
        status.value = "ready";
        return;
      }
      status.value = "completingLogin";
      const result = await options.api.request({
        method: "post",
        url: "/api/auth/complete-login",
        authCompletion: true,
      } as RequestConfig);
      assertCurrent(expected);
      if (result.status !== 204)
        throw new Error("Login completion must return 204");
      completedGeneration = expected;
      options.identifyAnalytics(session.value.analytics_id);
      status.value = "ready";
      message.value = "";
      if (callbackTarget) {
        const target = callbackTarget;
        callbackTarget = undefined;
        await options.navigate(target);
      }
    })().finally(() => {
      if (completing?.promise === promise) completing = undefined;
    });
    completing = { generation: expected, promise };
    return promise;
  };
  const reevaluateRole = (): Promise<void> => {
    const expected = generation;
    if (reevaluation?.generation === expected) return reevaluation.promise;
    roleRefreshPending = true;
    status.value = "completingLogin";
    const promise = (async () => {
      await options.reevaluateRoutes();
      assertCurrent(expected);
      roleRefreshPending = false;
      status.value = "ready";
      message.value = "";
    })().finally(() => {
      if (reevaluation?.promise === promise) reevaluation = undefined;
    });
    reevaluation = { generation: expected, promise };
    return promise;
  };
  const initialize = (): Promise<void> => {
    if (switching) return switching.promise;
    if (reevaluation?.generation === generation) return reevaluation.promise;
    if (initialization) return initialization;
    if (status.value === "ready") return Promise.resolve();
    const expected = generation;
    initialization = (async () => {
      try {
        const client = await sdk();
        cleanCallback();
        assertCurrent(expected);
        if (callbackError && !session.value) {
          status.value = "signInFailed";
          message.value = "Sign-in failed.";
          return;
        }
        if (!client.getUser()) {
          status.value = callbackPresent ? "signInFailed" : "signedOut";
          message.value = callbackPresent ? "Sign-in failed." : "";
          return;
        }
        if (roleRefreshPending) {
          await getAccessToken({ forceRefresh: true });
          assertCurrent(expected);
          await reevaluateRole();
          assertCurrent(expected);
          roleRefreshPending = false;
          status.value = "ready";
          message.value = "";
          return;
        }
        await getAccessToken(
          tokenRefreshPending ? { forceRefresh: true } : undefined,
        );
        assertCurrent(expected);
        await complete();
      } catch (error) {
        cleanCallback();
        if (expected !== generation) return;
        status.value = "retryableFailure";
        message.value = "Please try again.";
        if (callbackPresent && !session.value) {
          status.value = "signInFailed";
          message.value = "Sign-in failed.";
        }
      }
    })().finally(() => {
      initialization = undefined;
    });
    return initialization;
  };
  const signIn = async (
    entry: {
      returnTo?: unknown;
      invitationToken?: string;
      passwordResetToken?: string;
      context?: string;
    } = {},
  ) => {
    if (signingIn) return signingIn;
    const { returnTo, ...tokens } = entry;
    const reselect = status.value === "noOrganization";
    signingIn = (async () => {
      const pendingLogout = reselect ? signOut({ navigate: false }) : logout;
      const expected = generation;
      await pendingLogout;
      const client = await sdk();
      if (generation !== expected) return;
      await client.signIn({
        ...tokens,
        state: { returnTo: normalizeRedirectTarget(returnTo) ?? "/" },
      });
    })().finally(() => {
      signingIn = undefined;
    });
    return signingIn;
  };
  const switchOrganization = (organizationId: string): Promise<void> => {
    if (switching) {
      if (switching.organizationId !== organizationId)
        return Promise.reject(
          new Error("An organization switch is already pending"),
        );
      return switching.promise;
    }
    const subject = session.value?.sub;
    invalidate();
    const expected = generation;
    status.value = "initializing";
    const promise = (async () => {
      try {
        const client = await sdk();
        await client.switchToOrganization({ organizationId });
        assertCurrent(expected);
        await getAccessToken();
        if (
          session.value?.org_id !== organizationId ||
          session.value.sub !== subject
        )
          throw new Error("Organization switch was not established");
        await complete();
      } catch (error) {
        if (generation === expected) {
          status.value = "retryableFailure";
          message.value = "Please try again.";
        }
        throw error;
      }
    })().finally(() => {
      if (switching?.promise === promise) switching = undefined;
    });
    switching = { organizationId, promise };
    return promise;
  };

  options.api.interceptors.request.use(async (baseConfig) => {
    const config = baseConfig as RequestConfig;
    if (!config.authCompletion && status.value !== "ready")
      throw new axios.CanceledError("Tenant operations are stopped");
    const expected = config.authGeneration ?? generation;
    assertCurrent(expected);
    config.authGeneration = expected;
    const accessToken = await getAccessToken();
    assertCurrent(expected);
    config.headers.set("Authorization", `Bearer ${accessToken}`);
    const controller = new AbortController();
    controllers.add(controller);
    config.authController = controller;
    config.signal = config.signal
      ? AbortSignal.any([controller.signal, config.signal as AbortSignal])
      : controller.signal;
    return config;
  });
  options.api.interceptors.response.use(
    async (result) => {
      const config = result.config as RequestConfig;
      if (config.authController) controllers.delete(config.authController);
      assertCurrent(config.authGeneration!);
      const target = /\/api\/organization\/roles\/([^/]+)\/admin$/.exec(
        config.url ?? "",
      )?.[1];
      if (
        result.status === 204 &&
        ["post", "delete"].includes(config.method ?? "") &&
        target &&
        decodeURIComponent(target) === session.value?.sub
      ) {
        status.value = "completingLogin";
        roleRefreshPending = true;
        await getAccessToken({ forceRefresh: true });
        assertCurrent(config.authGeneration!);
        await reevaluateRole();
        assertCurrent(config.authGeneration!);
        roleRefreshPending = false;
        status.value = "ready";
      }
      return result;
    },
    async (error: unknown) => {
      if (!axios.isAxiosError(error) || !error.config) throw error;
      const config = error.config as RequestConfig;
      if (config.authController) controllers.delete(config.authController);
      assertCurrent(config.authGeneration!);
      if (error.response?.status !== 401) throw error;
      if (config.authRetry) {
        await signOut();
        throw error;
      }
      config.authRetry = true;
      await getAccessToken({ forceRefresh: true });
      assertCurrent(config.authGeneration!);
      return options.api.request(config);
    },
  );
  return {
    initialize,
    signIn,
    signOut,
    getAccessToken,
    switchOrganization,
    user,
    session,
    status,
    message,
    getGeneration: () => generation,
    isAuthenticated: computed(() => status.value === "ready"),
    isLoading: computed(() =>
      ["initializing", "completingLogin"].includes(status.value),
    ),
  };
};
